import {Box3, Camera, Group, Vector3, Vector2, MathUtils} from 'three'
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader'
import {gsap} from 'gsap'

import MonaGLB from '../../../scripts/assets/mona2.glb'
import {disposeObject3D} from '../../../scripts/modules/mona/mona-base'
import MainMona from './main-mona'
import type {MousePosition} from '../mouse-position'

export type HeroMonaOptions = {
  positionX: number
  rotationY: number
  scale: number
  startOpeningOnLoad: boolean
}

const DEFAULT_OPTIONS: HeroMonaOptions = {
  positionX: 1.3,
  rotationY: -Math.PI / 6.5,
  scale: 0.73,
  startOpeningOnLoad: true,
}

export default class HeroMona {
  monaGroup: Group = new Group()
  group: Group = new Group()
  mousePosition: MousePosition = {
    target: new Vector2(),
    current: new Vector2(),
  }
  cameraPosition: Vector3 = new Vector3()
  headCenterPosition: Vector3 = new Vector3()
  monaPosition: Vector3 = new Vector3()
  projectedPosition: Vector3 = new Vector3()
  mainMona: MainMona

  openingTl: gsap.core.Timeline
  reduceMotion: boolean = false
  #isSceneReady = false
  #shouldStartOpening: boolean
  #hasStartedOpening = false
  #hasCompletedOpening = false
  #isDisposed = false
  #onError?: (error: unknown) => void
  #onOpeningComplete?: () => void

  constructor({
    reduceMotion,
    options = DEFAULT_OPTIONS,
    onError,
    onOpeningComplete,
  }: {
    reduceMotion: boolean
    options?: HeroMonaOptions
    onError?: (error: unknown) => void
    onOpeningComplete?: () => void
  }) {
    this.reduceMotion = reduceMotion
    this.#shouldStartOpening = options.startOpeningOnLoad
    this.#onError = onError
    this.#onOpeningComplete = onOpeningComplete
    this.group.add(this.monaGroup)

    this.monaGroup.userData.rotation = {
      def: new Vector3(0, options.rotationY, 0),
      openingOffset: new Vector3(-0.15, 0, 0),
      current: new Vector3(),
    }

    this.monaGroup.position.set(options.positionX, -0.07, 0)
    if (!this.reduceMotion) {
      this.monaGroup.userData.rotation.openingOffset.y = -Math.PI * 2
    }

    this.mainMona = new MainMona()
    this.monaGroup.add(this.mainMona.group)

    this.openingTl = gsap.timeline({
      paused: true,
      delay: 1,
      onComplete: () => this.#completeOpening(),
    })

    this.monaGroup.scale.setScalar(options.scale)
    void this.#createScene()
  }

  handleMouseMove(e: MouseEvent, rect: DOMRect, camera: Camera) {
    if (this.reduceMotion) return

    camera.updateMatrixWorld()
    this.projectedPosition.copy(this.headCenterPosition)
    this.monaGroup.localToWorld(this.projectedPosition).project(camera)

    const centerX = rect.left + (this.projectedPosition.x * 0.5 + 0.5) * rect.width
    const centerY = rect.top + (-this.projectedPosition.y * 0.5 + 0.5) * rect.height
    const offsetX = e.clientX - centerX
    const offsetY = centerY - e.clientY
    const length = Math.hypot(offsetX, offsetY)

    let x = offsetX / rect.width
    let y = offsetY / rect.height

    x = Math.max(-0.5, Math.min(0.5, x))
    y = Math.max(-0.5, Math.min(0.5, y))

    const intensity = Math.min(1, Math.max(0, MathUtils.mapLinear(length, 500, 700, 1, 0)))
    x *= intensity
    y *= intensity

    this.mousePosition.target.set(x, y)
  }

  startOpening() {
    this.#shouldStartOpening = true
    this.#startOpeningIfReady()
  }

  async #createScene() {
    try {
      const gltf = await new GLTFLoader().loadAsync(MonaGLB)

      if (this.#isDisposed) {
        disposeObject3D(gltf.scene)
        return
      }

      this.mainMona.create(gltf.scene)
      this.monaGroup.updateWorldMatrix(true, true)
      new Box3().setFromObject(this.mainMona.monaHeadGroup).getCenter(this.headCenterPosition)
      this.monaGroup.worldToLocal(this.headCenterPosition)
      this.#isSceneReady = true

      if (this.reduceMotion) {
        this.#completeOpening()
        return
      }

      this.openingTl.fromTo(
        this.mainMona.group.position,
        {
          y: -2.7,
        },
        {
          y: 0,
          duration: 1.5,
          ease: 'elastic.out(1,0.5)',
        },
        0.3,
      )

      this.openingTl.to(
        this.monaGroup.userData.rotation.openingOffset,
        {
          y: 0,
          duration: 1.5,
          ease: 'expo.out',
        },
        0.1,
      )

      this.#startOpeningIfReady()
    } catch (error) {
      if (!this.#isDisposed) this.#onError?.(error)
    }
  }

  update(delta: number, camera: Camera) {
    const easingValue = Math.min(1, delta * 4)
    this.mousePosition.current.lerp(this.mousePosition.target, easingValue)

    camera.getWorldPosition(this.cameraPosition)
    this.monaGroup.getWorldPosition(this.monaPosition)
    const cameraFacingRotationY = Math.atan2(
      this.cameraPosition.x - this.monaPosition.x,
      this.cameraPosition.z - this.monaPosition.z,
    )

    this.mainMona.updateMouseInteraction(this.mousePosition, cameraFacingRotationY)
    this.rotationUpdate()
  }

  rotationUpdate() {
    const rotation = this.monaGroup.userData.rotation
    rotation.current.copy(rotation.def).add(rotation.openingOffset)

    this.monaGroup.rotation.set(rotation.current.x, rotation.current.y, rotation.current.z)
  }

  dispose() {
    this.#isDisposed = true
    this.openingTl.kill()
    this.mainMona.dispose()
    this.group.clear()
    this.monaGroup.clear()
  }

  #startOpeningIfReady() {
    if (this.reduceMotion || !this.#isSceneReady || !this.#shouldStartOpening || this.#hasStartedOpening) {
      return
    }

    this.#hasStartedOpening = true
    this.openingTl.play()
  }

  #completeOpening() {
    if (this.#hasCompletedOpening || this.#isDisposed) return

    this.#hasCompletedOpening = true
    this.#onOpeningComplete?.()
  }
}
